import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faCamera, faDesktop, faFileAlt, faMobileAlt, faPlusCircle, faQrcode, faTimes } from '@fortawesome/pro-solid-svg-icons';
// +++ Annotation Icons +++
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
// Import timezone plugin
import utc from 'dayjs/plugin/utc';
// Import utc plugin
import { Cropper, CropperRef } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import Webcam from 'react-webcam';
import styled from 'styled-components';

import { TagModel } from 'models/TagModel';

import { useWindowSize } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { Button, ComponentSizes, Icon, Input, Label } from 'core/ui';
import { MaskedDateTimeInputField } from 'core/ui/MaskedDateTimeInput/MaskedDateTimeInputField';
import { MASKED_DATETIME_INPUT_MODES } from 'core/ui/MaskedDateTimeInput/constants';

import { TagsField } from '../../tags/fragments/TagsField';
import {
  ImageFilters,
  calculatePreciseVideoCoordinates,
  debounce,
  getDefaultImageFilters,
  hasImageTransformations,
  transformImageData,
} from '../utils/imageTransformUtils';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// +++ START ImageCapture TYPE DEFINITIONS +++
// Define a basic interface for the ImageCapture functionality being used

// Define PhotoCapabilities if not globally available
type PhotoCapabilities = Record<string, unknown>;

interface ImageCaptureAPI {
  grabFrame: () => Promise<ImageBitmap>;
  getPhotoCapabilities?: () => Promise<PhotoCapabilities>;
  takePhoto?: () => Promise<Blob>; // Example
}

// Extend the global Window interface
interface WindowWithImageCapture extends Window {
  ImageCapture?: new (track: MediaStreamTrack) => ImageCaptureAPI; // Changed to function type constructor
}
// +++ END ImageCapture TYPE DEFINITIONS +++

// Define common timezones, with user's guessed timezone first and ensuring uniqueness
const commonTimezones = [
  dayjs.tz.guess(),
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Asia/Tokyo',
  'Australia/Sydney',
].filter((tz, index, self) => tz && self.indexOf(tz) === index); // Filter out potential undefined from guess and ensure unique

export interface FileUploadMetadata {
  tags: number[];
  filename: string;
  captureTime?: Date | null;
  eventTime?: Date | null;
  eventTimezone?: string;
  description?: string;
}

// ImageFilters interface now imported from utils

interface UploadProps {
  onFileUploaded?: (file: File | Blob, metadata?: FileUploadMetadata) => void;
  patientId: number;
  onGenerateQrCode: () => void;
  isGeneratingQrCode: boolean;
  availableTags: TagModel[];
  isLoadingTags: boolean;
  initialTagIds?: number[];
  onUploadComplete?: () => void;
  filterTagName?: string | null;
  hierarchicalPath?: string;
  worklistContext?: string | null;
  showDescriptionField?: boolean;
}

// +++ Annotation Types +++
interface Point {
  x: number;
  y: number;
}
type AnnotationTool = 'select' | 'pencil' | 'line'; // Add rect, text later?

interface Annotation {
  id: string;
  type: AnnotationTool;
  color: string;
  lineWidth: number;
  points: Point[];
}
// +++ End Annotation Types +++

// +++ Interface for file list item +++
interface FileUploadItem {
  id: string; // Unique ID for list key and removal
  file: File;
  previewUrl?: string; // Optional URL for image previews OR data URL for PDFs
  tags: number[]; // Add tags field, initially empty
  customFilename?: string; // For filename edits later
  captureTime?: Date | null; // Capture time from initial selection/capture
  captureDateString?: string; // For user edit of capture date
  captureTimeString?: string; // For user edit of capture time
  captureTimezone?: string; // Added for capture timezone selection
  eventDateString?: string; // Store date as string e.g., "MM/DD/YYYY"
  eventTimeString?: string; // Store time as string e.g., "hh:mm A"
  eventTimezone?: string; // Added for timezone selection
  fileType?: string; // To distinguish between images, PDFs, etc.
  description?: string;
  filters?: ImageFilters; // Added for image editing filters
}
// +++ End Interface +++

// ---------------------- Styled Components ----------------------
const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  height: 100%;
  min-height: 0;
  max-height: 100vh;
  overflow: hidden;
`;

const CustomControlsContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const EditorContainer = styled.div`
  margin-top: 15px;
  border: 1px solid #ccc;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: calc(100vh - 200px);
`;

const CropPreview = styled.div`
  flex: 1;
  width: 100%;
  position: relative;
  min-height: 400px;
  max-height: calc(100vh - 400px);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;

  .cropper {
    width: 100% !important;
    height: 100% !important;
  }
`;

const AnnotationCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  &.active {
    pointer-events: auto;
    cursor: crosshair;
  }
`;

const ConfirmButtonContainer = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  padding: 10px;
  border-top: 1px solid #ccc;
`;

// Simple Modal Styling (if core/ui Modal isn't available)
const WebcamModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;
const WebcamModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 90vw;
`;
const WebcamControls = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 10px;
`;

// +++ Added File List styles +++
const FileListContainer = styled.div`
  margin-top: 1rem;
  border: 1px dashed ${({ theme }) => theme.colors.borderBase || '#ccc'};
  padding: 15px;
  min-height: 100px;
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 300px);
  background-color: ${({ theme }) => theme.colors.palette?.grayscale?.[1] || '#f9f9f9'};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FileMetadataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px; // Increase gap for better spacing
  flex-grow: 1;
  // Remove margin, use gap on FileListItem instead
  // margin: 0 15px;
`;

const FileListItem = styled.div`
  display: flex;
  // align-items: center; // Align items to top for better vertical space
  align-items: flex-start;
  justify-content: space-between;
  padding: 15px 5px;
  border-bottom: 1px solid #eee;
  gap: 15px; // Add gap between main sections

  &:last-child {
    border-bottom: none;
  }

  /* Mobile adjustments */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9em;
  flex-shrink: 0; // Prevent thumbnail area from shrinking too much
`;

const FilePreview = styled.img`
  max-width: 200px;
  max-height: 200px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 3px;
  display: block;
  margin: 0 auto;
`;

const FileName = styled.span`
  // Truncate long filenames if needed
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px; // Adjust as needed
`;

const FileActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: flex-end; // Align buttons to the right

  /* Mobile adjustments */
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    order: -1; // Move to top on mobile
  }
`;

const TimeZoneSelector = styled.select`
  // Basic styling, can be replaced by core/ui Dropdown if available/preferred
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  background-color: white;
  margin-top: 5px; // Add some space above
  width: 100%; // Make it full width of its container
`;

const UploadAllButtonContainer = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
`;
// +++ End File List styles +++

// +++ Screen Capture Preview Styles +++
const CapturePreviewContainer = styled.div`
  position: relative; // For overlaying canvas
  margin-top: 15px;
  border: 1px solid #ccc;
  max-width: 100%;
  overflow: hidden; // Ensure video doesn't overflow container
  display: grid; // Use grid to stack video and canvas
`;

const CaptureVideo = styled.video`
  grid-area: 1 / 1; // Position in the same grid cell
  display: block; // Prevent extra space below video
  width: 100%;
  max-height: 50vh; // Limit preview height
`;

const DrawingCanvas = styled.canvas`
  grid-area: 1 / 1; // Position in the same grid cell
  position: relative; // Changed from absolute to relative for grid stacking
  width: 100%; // Ensure it tries to match video width
  height: 100%; // Ensure it tries to match video height
  cursor: crosshair;
  z-index: 1; // Ensure canvas is on top of the video for drawing
`;

const CaptureControls = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  background-color: #f0f0f0;
  border-top: 1px solid #ccc;
  flex-wrap: wrap;
`;
// +++ End Screen Capture Preview Styles +++

// +++ Annotation Toolbar Styles +++
const ToolbarContainer = styled.div`
  display: flex;
  gap: 5px;
  padding: 8px;
  background-color: #eee;
  border-bottom: 1px solid #ccc;
  flex-wrap: wrap;
`;

const ToolbarButton = styled(Button)<{ $active?: boolean }>`
  min-width: 40px;
  background-color: ${(props) => (props.$active ? props.theme.colors.primary : '#f8f9fa')};
  color: ${(props) => (props.$active ? 'white' : '#495057')};
  border: 1px solid ${(props) => (props.$active ? props.theme.colors.primary : '#dee2e6')};

  &:hover {
    /* Use primary color for active hover for now */
    background-color: ${(props) => (props.$active ? props.theme.colors.primary : '#e9ecef')};
  }
`;

const ColorInput = styled.input`
  width: 40px;
  height: 32px; // Match small button height approx
  border: 1px solid #ccc;
  padding: 2px;
  cursor: pointer;
`;
// +++ End Annotation Toolbar Styles +++

// +++ Moved Annotation Toolbar Component Definition Outside +++
interface AnnotationToolbarProps {
  currentTool: AnnotationTool;
  setCurrentTool: (tool: AnnotationTool) => void;
  currentColor: string;
  setCurrentColor: (color: string) => void;
}

const AnnotationToolbar: FC<AnnotationToolbarProps> = ({ currentTool, setCurrentTool, currentColor, setCurrentColor }) => (
  <div style={{ display: 'flex', gap: '10px', padding: '5px', background: '#f0f0f0', alignItems: 'center' }}>
    <select value={currentTool} onChange={(e) => setCurrentTool(e.target.value as AnnotationTool)}>
      <option value="select">Select</option>
      <option value="pencil">Pencil</option>
      <option value="line">Line</option>
    </select>
    <ColorInput type="color" value={currentColor} onChange={(e) => setCurrentColor(e.target.value)} title="Select Color" />
  </div>
);
// +++ End Moved Component Definition +++

// +++ Initial Mobile Upload Area Styles +++
const InitialUploadArea = styled.div<{ isDragging?: boolean }>`
  display: none; // Hidden by default, shown via logic in component
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  border: 2px dashed ${({ theme, isDragging }) => (isDragging ? theme.colors.primary : '#ccc')};
  border-radius: 8px;
  background-color: ${({ isDragging }) => (isDragging ? '#e9f5ff' : '#f9f9f9')};
  text-align: center;
  cursor: pointer;
  min-height: 200px;
  margin-top: 20px;
  transition:
    background-color 0.2s ease-in-out,
    border-color 0.2s ease-in-out;

  // Combined display logic: Show if (mobile AND no files) OR (desktop AND no files AND not screen capturing)
  // This will be handled in the component's JSX for clarity. Style is for appearance.

  &:hover {
    background-color: ${({ isDragging }) => (isDragging ? '#e9f5ff' : '#f0f0f0')};
    border-color: ${({ theme, isDragging }) => (isDragging ? theme.colors.primary : '#bbb')};
  }
`;

const InitialUploadIcon = styled(Icon)`
  font-size: 3em;
  color: #007bff; // Use primary color or theme color
  margin-bottom: 15px;
`;

const InitialUploadText = styled.p`
  font-size: 1.1em;
  color: #555;
  margin: 0;
`;
// +++ End Initial Mobile Upload Area Styles +++

const DateTimeFieldsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center; // Align items vertically

  // Make date/time picker take available space
  > div:first-child {
    flex-grow: 1;
  }
  > div:last-child {
    flex-grow: 1;
  }

  /* Responsive adjustments if needed */
  @media (max-width: 500px) {
    // Example breakpoint
    flex-direction: column;
    align-items: stretch;
  }
`;

// +++ Styled component for the session thumbnail strip +++
const SessionThumbnailStrip = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 10px 5px;
  background-color: #e9ecef; // A light background to differentiate
  border-top: 1px solid #ccc;
  gap: 10px;
  margin-top: 0; // Ensure it's snug under controls if controls are present
`;

const SessionThumbnailItem = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  width: 100px; // Fixed width for consistency
`;

const SessionThumbnailPreview = styled.img`
  max-width: 80px;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 3px;
  display: block;
  margin: 0 auto;
`;

const SessionThumbnailPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  border-radius: 3px;
`;

const SessionThumbnailFilename = styled.span`
  font-size: 0.75em;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;
// +++ End Styled component for session thumbnail strip +++

// +++ Image Filter Control Styles +++
const FilterControlsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 1rem 0;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
`;

const FilterControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 120px;
`;

const FilterLabel = styled.label`
  font-weight: bold;
  font-size: 0.9rem;
  color: #333;
`;

const FilterSlider = styled.input`
  width: 100%;
`;

const FilterButtonGroup = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const FilterButton = styled(Button)<{ $active?: boolean }>`
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  background-color: ${(props) => (props.$active ? '#007bff' : '#fff')};
  color: ${(props) => (props.$active ? '#fff' : '#000')};
  border: 1px solid ${(props) => (props.$active ? '#007bff' : '#ccc')};

  &:hover {
    background-color: ${(props) => (props.$active ? '#0056b3' : '#f8f9fa')};
  }
`;

const ResetButton = styled(Button)`
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #c82333;
  }
`;

// +++ Editor Layout Components +++
const EditorHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
`;

const EditorTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: #495057;
`;

const FilterControlsWrapper = styled.div`
  padding: 1rem;
  flex-shrink: 0;
`;

const EditorFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid #e9ecef;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  background-color: #f8f9fa;
`;
// +++ End Image Filter Control Styles +++

// ---------------------- Main Component -------------------------
export const Upload: FC<UploadProps> = ({
  onFileUploaded,
  patientId,
  onGenerateQrCode,
  isGeneratingQrCode,
  availableTags,
  isLoadingTags,
  initialTagIds = [],
  onUploadComplete,
  filterTagName,
  hierarchicalPath,
  worklistContext,
  showDescriptionField = false,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('cropped-image.png');
  const cropperRef = useRef<CropperRef | null>(null);
  const [editingFile, setEditingFile] = useState<FileUploadItem | null>(null);
  const [editingFilters, setEditingFilters] = useState<ImageFilters>(getDefaultImageFilters());

  // +++ State for multiple files +++
  const [filesToUpload, setFilesToUpload] = useState<FileUploadItem[]>([]);
  // +++ End State for multiple files +++

  const [isStartingCapture, setIsStartingCapture] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false); // New state for drag-over visual feedback

  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const webcamRef = useRef<Webcam>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // +++ State for Screen Capture Session +++
  const [screenCaptureStream, setScreenCaptureStream] = useState<MediaStream | null>(null);
  // Store track separately for ImageCapture
  const [screenCaptureTrack, setScreenCaptureTrack] = useState<MediaStreamTrack | null>(null);
  // +++ Add refs and state for drawing and video +++
  const videoRef = useRef<HTMLVideoElement>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectionRect, setSelectionRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  // State for video recording
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  // +++ State for screen capture session thumbnails +++
  const [sessionCapturedFiles, setSessionCapturedFiles] = useState<FileUploadItem[]>([]);
  // +++ End Add refs and state +++
  // +++ End Screen Capture Session State +++

  // +++ Annotation State +++
  const [currentTool, setCurrentTool] = useState<AnnotationTool>('select');
  const [currentColor, setCurrentColor] = useState<string>('#ff0000'); // Default red
  const [currentLineWidth, setCurrentLineWidth] = useState<number>(3);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [drawingPoints, setDrawingPoints] = useState<Point[]>([]); // For current drawing action
  const isAnnotating = currentTool !== 'select'; // Helper flag
  const annotationCanvasRef = useRef<HTMLCanvasElement>(null);
  // +++ End Annotation State +++

  const { width = 0 } = useWindowSize(); // Get window width for responsive logic
  const isMobile = width < 768; // Define mobile breakpoint

  // Derived state to get hierarchical tags for the TagsField
  const hierarchicalTags = useMemo(() => {
    // Use availableTags directly if they are already hierarchical, otherwise convert flat tags to hierarchy
    if (!availableTags || availableTags.length === 0) {
      return [];
    }

    // Check if availableTags already has hierarchical structure (has children property)
    const hasHierarchy = availableTags.some((tag) => tag.children && tag.children.length > 0);

    if (hasHierarchy) {
      // Already hierarchical, use as-is
      return availableTags;
    }

    // Convert flat tags to hierarchy
    const tagMap = new Map<number, TagModel>();
    const rootTags: TagModel[] = [];

    // First pass: create map of all tags
    availableTags.forEach((tag) => {
      tagMap.set(tag.id, { ...tag, children: [] });
    });

    // Second pass: build hierarchy
    availableTags.forEach((tag) => {
      const tagWithChildren = tagMap.get(tag.id);
      if (!tagWithChildren) return;

      if (tag.parentTagId && tagMap.has(tag.parentTagId)) {
        // This is a child tag
        const parent = tagMap.get(tag.parentTagId);
        parent?.children?.push(tagWithChildren);
      } else {
        // This is a root tag
        rootTags.push(tagWithChildren);
      }
    });

    return rootTags;
  }, [availableTags]);

  // Map flat tags for faster lookup
  const tagIdMap = useMemo(() => {
    const map = new Map<number, TagModel>();
    availableTags.forEach((tag) => {
      map.set(tag.id, tag);
    });
    return map;
  }, [availableTags]);

  const createFilenamePrefix = (basePrefix: string): string => {
    const now = new Date();
    // Use dayjs to format the date and time
    const formattedDate = dayjs(now).format('YYYYMMDD_HHmmss');
    const tagPart = filterTagName ? `${filterTagName.replace(/\s+/g, '')}_` : ''; // Remove spaces from tag name
    return `${tagPart}${basePrefix}_${formattedDate}`;
  };

  // +++ Image Filter Functions +++
  const updateEditingFilter = useCallback((filterName: keyof ImageFilters, value: number | boolean): void => {
    setEditingFilters((prev) => {
      // Reset crop selection when flipping to avoid coordinate confusion
      if (filterName === 'flipHorizontal' || filterName === 'flipVertical') {
        setTimeout(() => {
          if (cropperRef.current) {
            cropperRef.current.reset();
          }
        }, 100);
      }

      return {
        ...prev,
        [filterName]: value,
      };
    });
  }, []);

  const resetEditingFilters = useCallback((): void => {
    setEditingFilters(getDefaultImageFilters());
  }, []);

  const rotateEditingImage = useCallback((degrees: number): void => {
    setEditingFilters((prev) => {
      const newRotation = (prev.rotation + degrees) % 360;

      // Reset crop selection when rotating to avoid coordinate confusion
      setTimeout(() => {
        if (cropperRef.current) {
          // Reset the cropper to full image selection
          cropperRef.current.reset();
        }
      }, 100);

      return {
        ...prev,
        rotation: newRotation,
      };
    });
  }, []);

  // Using shared transformImageData utility for consistency

  // State for transformed image URL
  const [transformedImageSrc, setTransformedImageSrc] = useState<string | null>(null);

  // Debounced transformation function to prevent excessive re-rendering
  const debouncedTransform = useMemo(
    () =>
      debounce(async (imageUrl: string, filters: ImageFilters) => {
        try {
          const transformedUrl = await transformImageData(imageUrl, filters);
          setTransformedImageSrc((prevUrl) => {
            // Clean up previous transformed URL
            if (prevUrl && prevUrl !== imageUrl && prevUrl.startsWith('blob:')) {
              URL.revokeObjectURL(prevUrl);
            }
            return transformedUrl;
          });
        } catch (error: unknown) {
          console.error('Failed to transform image:', error);
          // Fallback to original image
          setTransformedImageSrc(imageUrl);
        }
      }, 150), // 150ms debounce delay
    [],
  );

  // Transform image when filters change
  useEffect(() => {
    if (!imageSrc) {
      setTransformedImageSrc(null);
      return;
    }

    // Check if any transformations are applied using shared utility
    if (!hasImageTransformations(editingFilters)) {
      // No transformations needed, use original
      setTransformedImageSrc(imageSrc);
      return;
    }

    // Apply debounced transformations
    debouncedTransform(imageSrc, editingFilters);
  }, [imageSrc, editingFilters, debouncedTransform]);

  // +++ End Image Filter Functions +++

  // --- Adjusted Cleanup Effect for imageSrc (cropper source) ---
  useEffect(() => {
    const currentImageSrc = imageSrc; // Capture current imageSrc for cleanup
    return () => {
      if (currentImageSrc) {
        console.log('Revoking cropper imageSrc:', currentImageSrc);
        URL.revokeObjectURL(currentImageSrc);
      }
    };
  }, [imageSrc]); // Run only when imageSrc changes

  // --- General Cleanup Effect for all file preview URLs on unmount ---
  useEffect(() => {
    return () => {
      console.log('Upload component unmounting. Revoking all applicable Object URLs.');
      filesToUpload.forEach((item) => {
        if (item.previewUrl && item.fileType && !item.fileType.includes('pdf') && item.previewUrl.startsWith('blob:')) {
          console.log('Revoking item.previewUrl on UNMOUNT (main list):_blob', item.previewUrl, item.id);
          URL.revokeObjectURL(item.previewUrl);
        }
      });
      sessionCapturedFiles.forEach((item) => {
        // Check if it's a blob URL and not already revoked (e.g. if it was also in filesToUpload)
        if (item.previewUrl && item.fileType && !item.fileType.includes('pdf') && item.previewUrl.startsWith('blob:')) {
          // A more robust check might be needed if items could be in session but not main and already handled.
          // For simplicity, we assume if it's here and a blob, it might need revoking.
          console.log('Revoking item.previewUrl on UNMOUNT (session list):_blob', item.previewUrl, item.id);
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount and unmount

  // +++ Effect to connect stream to video element +++
  useEffect(() => {
    const video = videoRef.current;
    if (video && screenCaptureStream) {
      console.log('Setting video srcObject');
      video.srcObject = screenCaptureStream;
      video.play().catch((err: unknown) => console.error('Video play failed:', err));
    } else if (video) {
      // Clear video source if stream stops
      video.srcObject = null;
    }
  }, [screenCaptureStream]);
  // +++ End Effect +++

  // +++ Effect to resize canvas to video dimensions +++
  useEffect(() => {
    const video = videoRef.current;
    const canvas = drawingCanvasRef.current;

    if (video && canvas && screenCaptureStream) {
      const resizeAndInitializeCanvas = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          console.log('=== CANVAS SIZING DEBUG ===');
          console.log('Video display size:', video.offsetWidth, 'x', video.offsetHeight);
          console.log('Video stream resolution:', video.videoWidth, 'x', video.videoHeight);
          console.log('Video aspect ratio:', video.videoWidth / video.videoHeight);
          console.log('Display aspect ratio:', video.offsetWidth / video.offsetHeight);

          // Set canvas internal resolution to match video display size exactly
          // This ensures perfect 1:1 coordinate mapping between mouse events and canvas
          canvas.width = video.offsetWidth;
          canvas.height = video.offsetHeight;

          // Ensure canvas display size matches its internal size
          canvas.style.width = `${video.offsetWidth}px`;
          canvas.style.height = `${video.offsetHeight}px`;

          console.log('Canvas internal size:', canvas.width, 'x', canvas.height);
          console.log('Canvas display size:', canvas.style.width, 'x', canvas.style.height);
          console.log('=== END CANVAS SIZING DEBUG ===');

          // Calculate video content area to help user see actual video bounds
          const videoAspectRatio = video.videoWidth / video.videoHeight;
          const displayAspectRatio = video.offsetWidth / video.offsetHeight;

          let contentArea: { x: number; y: number; w: number; h: number };

          if (videoAspectRatio > displayAspectRatio) {
            // Video is pillarboxed (black bars on sides)
            const actualWidth = video.offsetWidth;
            const actualHeight = video.offsetWidth / videoAspectRatio;
            const offsetY = (video.offsetHeight - actualHeight) / 2;
            contentArea = { x: 0, y: offsetY, w: actualWidth, h: actualHeight };
          } else {
            // Video is letterboxed (black bars on top/bottom)
            const actualHeight = video.offsetHeight;
            const actualWidth = video.offsetHeight * videoAspectRatio;
            const offsetX = (video.offsetWidth - actualWidth) / 2;
            contentArea = { x: offsetX, y: 0, w: actualWidth, h: actualHeight };
          }

          // Default selection to the video content area (excluding letterbox/pillarbox)
          const initialSelection = {
            x: Math.round(contentArea.x),
            y: Math.round(contentArea.y),
            w: Math.round(contentArea.w),
            h: Math.round(contentArea.h),
          };
          setSelectionRect(initialSelection);

          // Draw helpful guides and initial selection
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear first

            // Draw video content area boundary (light blue dashed line)
            ctx.setLineDash([5, 5]);
            ctx.strokeStyle = '#00bfff';
            ctx.lineWidth = 1;
            ctx.strokeRect(contentArea.x, contentArea.y, contentArea.w, contentArea.h);

            // Draw selection rectangle (solid red line)
            ctx.setLineDash([]);
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 2;
            ctx.strokeRect(initialSelection.x, initialSelection.y, initialSelection.w, initialSelection.h);

            console.log('Video content area:', contentArea);
            console.log('Initial selection:', initialSelection);
          }
        } else {
          // If video dimensions are not yet available, clear rect
          setSelectionRect(null);
          const ctx = canvas.getContext('2d');
          if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      };

      // Attempt initial resize and selection
      resizeAndInitializeCanvas();

      video.addEventListener('loadedmetadata', resizeAndInitializeCanvas);
      video.addEventListener('resize', resizeAndInitializeCanvas);
      window.addEventListener('resize', resizeAndInitializeCanvas);

      return () => {
        video.removeEventListener('loadedmetadata', resizeAndInitializeCanvas);
        video.removeEventListener('resize', resizeAndInitializeCanvas);
        window.removeEventListener('resize', resizeAndInitializeCanvas);
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSelectionRect(null); // Clear selection when stream stops or component unmounts
      };
    } else if (canvas) {
      // If no stream, ensure canvas is clear and selectionRect is null
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSelectionRect(null);
    }
    return undefined;
  }, [screenCaptureStream]);
  // +++ End Effect +++

  const handleScreenCaptureClick = useCallback(async () => {
    if (isStartingCapture) return;
    setIsStartingCapture(true);
    setImageSrc(null);

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { mediaSource: 'screen' } as MediaTrackConstraints,
        audio: false,
      });

      const track = stream.getVideoTracks()[0];
      const ImageCaptureConstructor = (window as WindowWithImageCapture).ImageCapture;

      if (!ImageCaptureConstructor) {
        throw new Error('ImageCapture API is not supported in this browser.');
      }
      const imageCapture = new ImageCaptureConstructor(track);

      track.onended = () => {
        stream.getTracks().forEach((t) => t.stop());
        setIsStartingCapture(false);
      };

      await new Promise((resolve) => setTimeout(resolve, 200));

      // Use 'as any' for grabFrame method
      const bitmap = await imageCapture.grabFrame();
      track.stop();

      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('bitmaprenderer');
      if (ctx) {
        ctx.transferFromImageBitmap(bitmap);
      } else {
        const ctx2d = canvas.getContext('2d');
        if (!ctx2d) throw new Error('Canvas context not available');
        ctx2d.drawImage(bitmap, 0, 0);
      }

      canvas.toBlob((blob: Blob | null) => {
        if (blob) {
          const newFileName = `screenshot-${Date.now()}.png`;
          const objectURL = URL.createObjectURL(blob);
          setFileName(newFileName);
          setImageSrc(objectURL);
        } else {
          console.error('Failed to create blob from screen capture canvas.');
        }
        setIsStartingCapture(false);
      }, 'image/png');
    } catch (error) {
      console.error('Screen capture error:', error);
      if ((error as DOMException).name === 'NotAllowedError') {
        alert('Screen sharing permission denied. Please allow screen sharing in your browser settings.');
      } else {
        alert('Could not start screen capture.');
      }
      setIsStartingCapture(false);
    }
  }, [isStartingCapture]);

  // --- Handler for Custom 'My Device' Button ---
  const handleMyDeviceClick = () => {
    console.log('handleMyDeviceClick called'); // Log when called
    console.log('fileInputRef.current:', fileInputRef.current); // Log the ref
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error('File input ref not found!');
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const files = Array.from(event.target.files);
    processAndAddFiles(files);
    if (event.target) event.target.value = ''; // Reset file input
  };

  // Refactored file processing logic to be reusable
  const processAndAddFiles = useCallback(
    (incomingFiles: File[], isFromSessionCapture = false) => {
      const timeAdded = new Date();
      const currentTz = dayjs.tz.guess() || 'UTC';
      let initialDateTimeForStrings: Date;
      if (!isMobile) {
        initialDateTimeForStrings = dayjs(timeAdded).tz(currentTz).startOf('day').toDate();
      } else {
        initialDateTimeForStrings = timeAdded;
      }

      const newUploadItems: FileUploadItem[] = [];
      const pdfProcessingPromises: Promise<void>[] = [];

      incomingFiles.forEach((file) => {
        const newItem: FileUploadItem = {
          id: crypto.randomUUID(),
          file: file,
          fileType: file.type,
          // FIXED: Use initialTagIds if available and not loading, otherwise start with empty array
          // The effect below will update files when initialTagIds becomes available
          tags: initialTagIds && initialTagIds.length > 0 && !isLoadingTags ? initialTagIds : [],
          captureTime: timeAdded,
          captureDateString: dayjs(timeAdded).tz(currentTz).format('MM/DD/YYYY'),
          captureTimeString: dayjs(timeAdded).tz(currentTz).format('hh:mm A'),
          captureTimezone: currentTz,
          eventDateString: dayjs(initialDateTimeForStrings).tz(currentTz).format('MM/DD/YYYY'),
          eventTimeString: dayjs(initialDateTimeForStrings).tz(currentTz).format('hh:mm A'),
          eventTimezone: currentTz,
          description: '',
          filters: file.type.startsWith('image/')
            ? {
                brightness: 100,
                contrast: 100,
                saturation: 100,
                rotation: 0,
                flipHorizontal: false,
                flipVertical: false,
              }
            : undefined,
        };

        if (file.type === 'application/pdf') {
          // newItem is created without previewUrl here for PDFs
          const promise = new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const dataUrl = reader.result as string;
              // Update filesToUpload: find the item by ID and update its previewUrl
              setFilesToUpload((prev) => prev.map((item) => (item.id === newItem.id ? { ...item, previewUrl: dataUrl } : item)));
              if (isFromSessionCapture) {
                // Update sessionCapturedFiles similarly
                setSessionCapturedFiles((prev) => prev.map((item) => (item.id === newItem.id ? { ...item, previewUrl: dataUrl } : item)));
              }
              resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          pdfProcessingPromises.push(promise);
        } else if (file.type.startsWith('image/')) {
          newItem.previewUrl = URL.createObjectURL(file);
        } else {
          // For other file types (e.g., video), no previewUrl by default
        }
        newUploadItems.push(newItem); // Add all items (PDFs without preview, images with) to the temporary list
      });

      // Add all newly created items to the main filesToUpload list
      if (newUploadItems.length > 0) {
        setFilesToUpload((prevFiles) => {
          // Simpler append for debugging - assumes newUploadItems are always new
          return [...prevFiles, ...newUploadItems];
        });
      }

      // Add all newly created items to the session list if applicable
      if (isFromSessionCapture && newUploadItems.length > 0) {
        setSessionCapturedFiles((prevSessionFiles) => {
          const newItemsToAdd = newUploadItems.filter((newItem) => !prevSessionFiles.some((prevItem) => prevItem.id === newItem.id));
          return [...prevSessionFiles, ...newItemsToAdd];
        });
      }

      if (pdfProcessingPromises.length > 0) {
        Promise.all(pdfProcessingPromises).catch((error: unknown) => {
          console.error('Error processing PDF files for preview:', error);
          NotificationsService.displayError('Could not generate preview for one or more PDF files.');
        });
      }
    },
    [initialTagIds, isLoadingTags, isMobile, setFilesToUpload, sessionCapturedFiles],
  );

  // ADDED: Effect to update existing files with initialTagIds when they become available
  useEffect(() => {
    // Only apply initial tags if:
    // 1. We have initialTagIds
    // 2. Tags are not loading
    // 3. We have files to update
    if (initialTagIds && initialTagIds.length > 0 && !isLoadingTags && filesToUpload.length > 0) {
      console.log('[Upload] Applying delayed initial tag IDs to existing files:', initialTagIds);

      setFilesToUpload((prevFiles) =>
        prevFiles.map((item) => {
          // Only update files that don't have tags yet (to avoid overwriting user selections)
          if (item.tags.length === 0) {
            console.log(`[Upload] Updating file ${item.file.name} with initial tags:`, initialTagIds);
            return { ...item, tags: [...initialTagIds] };
          }
          return item;
        }),
      );

      // Also update session captured files if any
      setSessionCapturedFiles((prevFiles) =>
        prevFiles.map((item) => {
          if (item.tags.length === 0) {
            return { ...item, tags: [...initialTagIds] };
          }
          return item;
        }),
      );
    }
  }, [initialTagIds, isLoadingTags, filesToUpload.length]); // Note: filesToUpload.length to avoid infinite loops

  const handleCameraClick = () => {
    setImageSrc(null);
    setShowWebcamModal(true);
  };

  const handleWebcamCapture = useCallback(() => {
    if (!webcamRef.current) return;
    const imageSrcBase64 = webcamRef.current.getScreenshot();
    if (imageSrcBase64) {
      fetch(imageSrcBase64)
        .then((res) => res.blob())
        .then((blob) => {
          const prefix = createFilenamePrefix('Camera');
          const newFileName = `${prefix}_${dayjs().format('YYYYMMDD_HHmmss')}.jpeg`;
          const newFile = new File([blob], newFileName, { type: 'image/jpeg' });
          processAndAddFiles([newFile]); // Use the new central processor
          setShowWebcamModal(false);
        })
        .catch((err: unknown) => {
          console.error('Error converting webcam image to blob:', err);
          setShowWebcamModal(false);
        });
    } else {
      console.warn('Could not get screenshot from webcam.');
      setShowWebcamModal(false);
    }
  }, [createFilenamePrefix, processAndAddFiles]);

  const handleWebcamClose = () => {
    setShowWebcamModal(false);
  };

  const getAllowedFileTypes = () => {
    // Allow any file type for upload, preview handled separately
    return undefined; // Or specify broad types like "*/*"
  };

  const handleConfirmCrop = useCallback(() => {
    const cropCanvas = cropperRef.current?.getCanvas();

    if (cropCanvas && editingFile) {
      // Simply export the cropped canvas - filters are already applied visually and saved in metadata
      cropCanvas.toBlob(
        (blob) => {
          if (blob) {
            const croppedFile = new File([blob], editingFile.file.name, { type: blob.type });
            setFilesToUpload((prevFiles) =>
              prevFiles.map((item) =>
                item.id === editingFile.id
                  ? {
                      ...item,
                      file: croppedFile,
                      previewUrl: URL.createObjectURL(croppedFile),
                      filters: editingFilters, // Save filter metadata for server
                    }
                  : item,
              ),
            );
            setEditingFile(null);
            setImageSrc(null);
            setAnnotations([]);
            resetEditingFilters();
          } else {
            console.error('Failed to create blob from cropped canvas.');
          }
        },
        'image/png',
        0.9,
      );
    } else {
      console.error('Cropper canvas or editing file not found.');
      setEditingFile(null);
    }
  }, [editingFile, editingFilters, setFilesToUpload, resetEditingFilters]);

  // +++ Handler to update filename or tags for a specific file +++
  const handleFileMetadataChange = useCallback(
    (
      id: string,
      field:
        | 'customFilename'
        | 'tags'
        | 'captureTime'
        | 'captureDateString'
        | 'captureTimeString'
        | 'captureTimezone'
        | 'eventDateString'
        | 'eventTimeString'
        | 'eventTimezone'
        | 'description',
      value: string | number[] | Date | null,
    ) => {
      setFilesToUpload((prevFiles) =>
        prevFiles.map((item) => {
          if (item.id === id) {
            // Ensure captureTime remains Date | null, others are string or number[]
            if (field === 'captureTime') {
              // This case might be redundant if we primarily edit strings
              return { ...item, captureTime: value instanceof Date ? value : item.captureTime };
            } else if (field === 'tags') {
              return { ...item, tags: Array.isArray(value) ? value : item.tags };
            } else if (typeof value === 'string') {
              // All other string fields
              return { ...item, [field]: value };
            }
          }
          return item;
        }),
      );
    },
    [],
  );
  // +++ End Handler +++

  // +++ Handler to Remove File from List +++
  const handleRemoveFile = useCallback(
    (idToRemove: string) => {
      setFilesToUpload((prevFiles) => {
        const fileToRemove = prevFiles.find((item) => item.id === idToRemove);
        if (fileToRemove?.previewUrl && fileToRemove.fileType && !fileToRemove.fileType.includes('pdf') && fileToRemove.previewUrl.startsWith('blob:')) {
          console.log('Revoking previewUrl for removed file (main list):_blob', fileToRemove.previewUrl, fileToRemove.id);
          URL.revokeObjectURL(fileToRemove.previewUrl);
        }
        return prevFiles.filter((item) => item.id !== idToRemove);
      });
      // Also remove from session captured files if present
      setSessionCapturedFiles((prevSessionFiles) => {
        const sessionFileToRemove = prevSessionFiles.find((item) => item.id === idToRemove);
        if (
          sessionFileToRemove?.previewUrl &&
          sessionFileToRemove.fileType &&
          !sessionFileToRemove.fileType.includes('pdf') &&
          sessionFileToRemove.previewUrl.startsWith('blob:')
        ) {
          // Avoid double revocation if it was the same URL as in main list and already revoked.
          // This check is simplistic; assumes if it's a blob URL it should be revoked if item is removed from session list.
          // A more robust system might track URL users or ensure URLs are unique per list item context.
          const mainListFile = filesToUpload.find((f) => f.id === idToRemove); // Check current main list state
          if (!mainListFile || mainListFile.previewUrl !== sessionFileToRemove.previewUrl) {
            console.log('Revoking previewUrl for removed file (session list):_blob', sessionFileToRemove.previewUrl, sessionFileToRemove.id);
            URL.revokeObjectURL(sessionFileToRemove.previewUrl);
          }
        }
        return prevSessionFiles.filter((item) => item.id !== idToRemove);
      });
    },
    [filesToUpload],
  ); // filesToUpload is needed to check for already revoked URLs from main list context
  // +++ End Handler to Remove File +++

  // +++ Handler to Upload All Files +++
  const handleUploadAllClick = async () => {
    if (filesToUpload.length === 0) {
      NotificationsService.displayInfo('No files to upload.');
      return;
    }
    try {
      for (const item of filesToUpload) {
        const fileToUpload: File | Blob = item.file;

        // Note: The original logic for cropping/annotating a single image before upload
        // needs to be adapted. This example assumes the file `item.file` is the final version.
        // A more complex implementation would apply stored transformations here.

        if (onFileUploaded) {
          const metadata: FileUploadMetadata = {
            filename: item.customFilename || item.file.name,
            tags: item.tags,
            captureTime: item.captureTime,
            eventTime:
              item.eventDateString && item.eventTimeString
                ? dayjs(`${item.eventDateString} ${item.eventTimeString}`, 'MM/DD/YYYY hh:mm A').toDate()
                : undefined,
            eventTimezone: item.eventTimezone,
            description: item.description,
          };
          onFileUploaded(fileToUpload, metadata);
        }
      }

      NotificationsService.displaySuccess(`${filesToUpload.length} file(s) uploaded successfully.`);
      setFilesToUpload([]); // Clear the list after upload
      setSessionCapturedFiles([]); // Clear session captures too
      onUploadComplete?.(); // Correct usage of the prop from destructuring
    } catch (error) {
      console.error('An error occurred during bulk upload:', error);
      NotificationsService.displayError('One or more files failed to upload.');
    }
  };
  // +++ End Handler to Upload All +++

  // --- Stop Screen Capture Function ---
  const handleStopScreenCapture = useCallback(() => {
    if (screenCaptureStream) {
      screenCaptureStream.getTracks().forEach((track) => track.stop());
    }
    setScreenCaptureStream(null);
    setScreenCaptureTrack(null);
    setIsStartingCapture(false); // Reset button state
    setSessionCapturedFiles([]); // Clear session-specific captures
  }, [screenCaptureStream]);
  // --- End Stop Screen Capture Function ---

  // --- Start/Toggle Screen Capture Function ---
  const handleScreenCaptureToggle = useCallback(async () => {
    // If stream exists, stop it
    if (screenCaptureStream) {
      handleStopScreenCapture();
      return;
    }

    // Otherwise, start a new stream
    if (isStartingCapture) return; // Prevent double clicks
    setIsStartingCapture(true);

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          mediaSource: 'screen',
          selfBrowserSurface: 'exclude', // Hint to exclude current tab
          displaySurface: 'monitor', // Hint to prefer entire screen
        } as MediaTrackConstraints,
        audio: false,
      });
      const track = stream.getVideoTracks()[0];

      // Handle user stopping sharing from browser UI
      track.onended = () => {
        console.log('Screen share stopped via browser UI.');
        handleStopScreenCapture(); // Clean up state
      };

      setScreenCaptureStream(stream);
      setScreenCaptureTrack(track);
      setIsStartingCapture(false); // Ready to take snapshots
    } catch (error) {
      console.error('Screen capture error:', error);
      if ((error as DOMException).name === 'NotAllowedError') {
        alert('Screen sharing permission denied. Please allow screen sharing in your browser settings.');
      } else {
        alert('Could not start screen capture.');
      }
      handleStopScreenCapture(); // Ensure cleanup on error
    }
  }, [isStartingCapture, screenCaptureStream, handleStopScreenCapture]);
  // --- End Start/Toggle Screen Capture Function ---

  // +++ Canvas Drawing Handlers +++
  const getMousePos = useCallback((canvas: HTMLCanvasElement, e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (e.button !== 0 || !drawingCanvasRef.current) return;
      const pos = getMousePos(drawingCanvasRef.current, e);
      setIsDrawing(true);
      setSelectionRect({ x: pos.x, y: pos.y, w: 0, h: 0 });
    },
    [getMousePos],
  );

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawing || !selectionRect || !drawingCanvasRef.current || !videoRef.current) return;
      const canvas = drawingCanvasRef.current;
      const video = videoRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const currentPos = getMousePos(canvas, e);
      const newRect = {
        ...selectionRect,
        w: currentPos.x - selectionRect.x,
        h: currentPos.y - selectionRect.y,
      };
      setSelectionRect(newRect);

      // Clear and redraw everything
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Redraw video content area boundary if video is available
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        const videoAspectRatio = video.videoWidth / video.videoHeight;
        const displayAspectRatio = video.offsetWidth / video.offsetHeight;

        let contentArea: { x: number; y: number; w: number; h: number };

        if (videoAspectRatio > displayAspectRatio) {
          const actualWidth = video.offsetWidth;
          const actualHeight = video.offsetWidth / videoAspectRatio;
          const offsetY = (video.offsetHeight - actualHeight) / 2;
          contentArea = { x: 0, y: offsetY, w: actualWidth, h: actualHeight };
        } else {
          const actualHeight = video.offsetHeight;
          const actualWidth = video.offsetHeight * videoAspectRatio;
          const offsetX = (video.offsetWidth - actualWidth) / 2;
          contentArea = { x: offsetX, y: 0, w: actualWidth, h: actualHeight };
        }

        // Draw video content area boundary (light blue dashed line)
        ctx.setLineDash([5, 5]);
        ctx.strokeStyle = '#00bfff';
        ctx.lineWidth = 1;
        ctx.strokeRect(contentArea.x, contentArea.y, contentArea.w, contentArea.h);
      }

      // Draw selection rectangle (solid red line)
      ctx.setLineDash([]);
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 2;
      ctx.strokeRect(newRect.x, newRect.y, newRect.w, newRect.h);
    },
    [isDrawing, selectionRect, getMousePos],
  );

  const handleCanvasMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawing || !selectionRect || !drawingCanvasRef.current || !videoRef.current) return;
      setIsDrawing(false);

      // Normalize rectangle (top-left corner, positive width/height)
      const canvas = drawingCanvasRef.current;
      const video = videoRef.current;
      const ctx = canvas.getContext('2d');
      // Use const and modify properties directly if needed, or create new obj
      const currentRect = { ...selectionRect };

      if (currentRect.w < 0) {
        currentRect.x += currentRect.w;
        currentRect.w = Math.abs(currentRect.w);
      }
      if (currentRect.h < 0) {
        currentRect.y += currentRect.h;
        currentRect.h = Math.abs(currentRect.h);
      }
      const finalRect = currentRect; // Assign to const after modifications

      if (finalRect.w === 0 || finalRect.h === 0) {
        // If width/height is zero (just a click), clear the selection
        setSelectionRect(null);
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      } else {
        setSelectionRect(finalRect);
        // Redraw everything with the final normalized rectangle
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // Redraw video content area boundary if video is available
          if (video.videoWidth > 0 && video.videoHeight > 0) {
            const videoAspectRatio = video.videoWidth / video.videoHeight;
            const displayAspectRatio = video.offsetWidth / video.offsetHeight;

            let contentArea: { x: number; y: number; w: number; h: number };

            if (videoAspectRatio > displayAspectRatio) {
              const actualWidth = video.offsetWidth;
              const actualHeight = video.offsetWidth / videoAspectRatio;
              const offsetY = (video.offsetHeight - actualHeight) / 2;
              contentArea = { x: 0, y: offsetY, w: actualWidth, h: actualHeight };
            } else {
              const actualHeight = video.offsetHeight;
              const actualWidth = video.offsetHeight * videoAspectRatio;
              const offsetX = (video.offsetWidth - actualWidth) / 2;
              contentArea = { x: offsetX, y: 0, w: actualWidth, h: actualHeight };
            }

            // Draw video content area boundary (light blue dashed line)
            ctx.setLineDash([5, 5]);
            ctx.strokeStyle = '#00bfff';
            ctx.lineWidth = 1;
            ctx.strokeRect(contentArea.x, contentArea.y, contentArea.w, contentArea.h);
          }

          // Draw selection rectangle (solid red line)
          ctx.setLineDash([]);
          ctx.strokeStyle = 'red';
          ctx.lineWidth = 2;
          ctx.strokeRect(finalRect.x, finalRect.y, finalRect.w, finalRect.h);
        }
      }
    },
    [isDrawing, selectionRect],
  );
  // +++ End Canvas Drawing Handlers +++

  // +++ Capture Area Handler (Implement actual capture) +++
  const handleCaptureAreaClick = useCallback(() => {
    const video = videoRef.current;
    const drawCanvas = drawingCanvasRef.current;
    if (!selectionRect || selectionRect.w === 0 || selectionRect.h === 0 || !video || !drawCanvas) {
      alert('Please select a valid area on the preview first.');
      return;
    }

    console.log('=== CAPTURE DEBUG START ===');
    console.log('Raw selection rect:', selectionRect);
    console.log('Video element dimensions (offsetWidth x offsetHeight):', video.offsetWidth, 'x', video.offsetHeight);
    console.log('Video stream dimensions (videoWidth x videoHeight):', video.videoWidth, 'x', video.videoHeight);

    // Use precise coordinate calculation utility
    const { sourceX, sourceY, sourceWidth, sourceHeight, scaleX, scaleY } = calculatePreciseVideoCoordinates(selectionRect, video, drawCanvas);

    console.log('Scale factors - X:', scaleX, 'Y:', scaleY);
    console.log('Final video capture coords:');
    console.log('  Source X:', sourceX, 'Y:', sourceY);
    console.log('  Source Width:', sourceWidth, 'Height:', sourceHeight);
    console.log('  Video dimensions:', video.videoWidth, 'x', video.videoHeight);

    // Validate that we have a reasonable capture area
    if (sourceWidth <= 0 || sourceHeight <= 0) {
      console.error('Invalid capture dimensions:', sourceWidth, 'x', sourceHeight);
      alert('Invalid capture area. Please select a valid region.');
      return;
    }

    // Create canvas at the capture resolution
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = sourceWidth;
    tempCanvas.height = sourceHeight;
    const ctx = tempCanvas.getContext('2d');

    console.log('Created capture canvas:', tempCanvas.width, 'x', tempCanvas.height);

    if (ctx) {
      // Clear the canvas first
      ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

      console.log('Drawing with precise coordinates:');
      console.log('  Source - X:', sourceX, 'Y:', sourceY);
      console.log('  Size - W:', sourceWidth, 'H:', sourceHeight);

      // Draw the selected area from the video
      ctx.drawImage(
        video,
        sourceX, // source x
        sourceY, // source y
        sourceWidth, // source width
        sourceHeight, // source height
        0, // destination x
        0, // destination y
        sourceWidth, // destination width
        sourceHeight, // destination height
      );

      console.log('Drew image from video to canvas');
      console.log('=== CAPTURE DEBUG END ===');

      tempCanvas.toBlob((blob) => {
        if (blob) {
          const prefix = createFilenamePrefix('Capture');
          const newFileName = `${prefix}_${dayjs().format('YYYYMMDD_HHmmss')}.png`;
          const newFile = new File([blob], newFileName, { type: 'image/png' });
          processAndAddFiles([newFile], true); // Pass true for session capture

          NotificationsService.displaySuccess('Screen area captured successfully!');
          console.log('Captured area added to list with dimensions:', sourceWidth, 'x', sourceHeight);
        } else {
          console.error('Failed to create blob from captured area canvas.');
          alert('Could not capture selected area.');
        }
      }, 'image/png');
    } else {
      console.error('Failed to get 2D context from temp canvas for capture.');
      alert('Could not initialize capture canvas.');
    }
  }, [selectionRect, createFilenamePrefix, processAndAddFiles]);
  // +++ End Capture Area Handler +++

  // +++ Video Recording Handlers (Implement) +++
  const handleDataAvailable = useCallback((event: BlobEvent) => {
    if (event.data.size > 0) {
      console.log('Recording data available:', event.data.size);
      recordedChunksRef.current.push(event.data);
    }
  }, []);

  const handleStartRecording = useCallback(() => {
    if (!screenCaptureStream || isRecording) {
      console.warn('Start recording called but stream not ready or already recording');
      return;
    }
    recordedChunksRef.current = []; // Clear previous chunks
    try {
      // Determine preferred mimeType
      const mimeTypes = ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4', ''];

      // Find the first supported mimeType
      let chosen = mimeTypes.find((type) => MediaRecorder.isTypeSupported(type));
      if (chosen) {
        if (chosen) console.log(`Using mimeType: "${chosen}"`);
      } else {
        console.warn('No specific mimeType supported; using browser default');
        chosen = '';
      }
      const options = { mimeType: chosen };

      console.log('Attempting to start MediaRecorder with stream:', screenCaptureStream);
      mediaRecorderRef.current = new MediaRecorder(screenCaptureStream, options.mimeType ? options : undefined);
      mediaRecorderRef.current.ondataavailable = handleDataAvailable;

      mediaRecorderRef.current.onstop = () => {
        console.log('Recording stopped. Processing chunks...');
        const mimeType = mediaRecorderRef.current?.mimeType || 'video/mp4'; // Default guess
        const videoBlob = new Blob(recordedChunksRef.current, { type: mimeType });
        const timeAdded = new Date(); // Get timestamp now
        const currentTz = dayjs.tz.guess() || 'UTC';

        if (videoBlob.size > 0) {
          const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.split('/')[1]?.split(';')[0] || 'mp4'; // Handle mp4 extension
          // Use Recording prefix and timestamp format
          const prefix = createFilenamePrefix('Recording');
          const newFileName = `${prefix}_${dayjs().format('YYYYMMDD_HHmmss')}.${ext}`;
          const newFile = new File([videoBlob], newFileName, { type: mimeType });
          processAndAddFiles([newFile], true); // Pass true for session capture
          NotificationsService.displaySuccess('Video recording captured successfully!');
          console.log('Video recording added to list:', newFileName);
        } else {
          console.warn('Recorded video blob is empty.');
          alert('Recording failed or produced an empty file.');
        }

        recordedChunksRef.current = [];
        setIsRecording(false);
        mediaRecorderRef.current = null; // Clean up recorder instance
      };

      mediaRecorderRef.current.onerror = (event: Event) => {
        console.error('MediaRecorder error:', event);
        // Access specific error details if needed, casting cautiously
        const errorEvent = event as ErrorEvent; // Example cast
        alert(`Recording error occurred: ${errorEvent?.error?.name || 'Unknown error'}`);
        // Ensure state is reset on error during recording
        if (mediaRecorderRef.current?.state !== 'inactive') {
          try {
            mediaRecorderRef.current?.stop();
          } catch (e) {
            console.error('Error stopping recorder on error:', e);
          }
        }
        setIsRecording(false);
        mediaRecorderRef.current = null;
        recordedChunksRef.current = [];
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      console.log('Started recording with options:', options);
      // Clear any drawn rectangle when recording starts
      const drawCtx = drawingCanvasRef.current?.getContext('2d');
      if (drawCtx && drawingCanvasRef.current) {
        drawCtx.clearRect(0, 0, drawingCanvasRef.current.width, drawingCanvasRef.current.height);
      }
      setSelectionRect(null);
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert(`Could not start video recording: ${(error as Error).message}`);
      setIsRecording(false);
      // Clean up if recorder was partially created
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.onerror = null;
        mediaRecorderRef.current = null;
      }
    }
  }, [screenCaptureStream, isRecording, handleDataAvailable, createFilenamePrefix, processAndAddFiles]);

  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      console.log('Stopping recording via button...');
      mediaRecorderRef.current.stop();
      // onstop handler will set isRecording to false and clean up
    } else {
      console.warn('Attempted to stop recording, but recorder state is not recording');
      // Force state reset if something went wrong
      setIsRecording(false);
      mediaRecorderRef.current = null;
      recordedChunksRef.current = [];
    }
  }, []);
  // +++ End Video Recording Handlers +++

  // +++ Edit File Handler +++
  const handleEditFile = useCallback(
    (idToEdit: string) => {
      const fileItem = filesToUpload.find((item) => item.id === idToEdit);
      if (fileItem) {
        // Revoke the *previous* cropper-specific imageSrc, if one existed
        if (imageSrc) {
          URL.revokeObjectURL(imageSrc);
        }

        // Create a new ObjectURL specifically for the cropper from the item's file data
        // Always use the original file data to avoid black images on re-edit
        const cropperObjectURL = URL.createObjectURL(fileItem.file);

        // Add a small delay to ensure the previous image is fully cleaned up
        setTimeout(() => {
          setImageSrc(cropperObjectURL); // imageSrc is now unique to the cropper session
          setEditingFile(fileItem);
          setAnnotations([]); // Clear any previous annotations when starting a new edit

          // Initialize editing filters with the file's current filters or defaults
          setEditingFilters(
            fileItem.filters || {
              brightness: 100,
              contrast: 100,
              saturation: 100,
              rotation: 0,
              flipHorizontal: false,
              flipVertical: false,
            },
          );
        }, 50);
      } else {
        console.warn('Could not find file or preview URL to edit');
      }
    },
    [filesToUpload, imageSrc], // imageSrc is a dep because we read it to revoke it.
  );
  // +++ End Edit File Handler +++

  const processDroppedFiles = (droppedFiles: FileList) => {
    if (!droppedFiles || droppedFiles.length === 0) return;
    processAndAddFiles(Array.from(droppedFiles)); // Use the new central processor
  };

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDraggingOver(false);
      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        // processDroppedFiles(event.dataTransfer.files);
        // Directly use processAndAddFiles with the FileList converted to an array
        processAndAddFiles(Array.from(event.dataTransfer.files));
        event.dataTransfer.clearData();
      }
    },
    [processAndAddFiles], // Corrected dependency
  );

  return (
    <UploadContainer>
      {/* Hidden file input - Moved outside conditional rendering */}
      <HiddenFileInput type="file" ref={fileInputRef} onChange={handleFileInputChange} multiple accept={getAllowedFileTypes()} />

      {/* Custom Controls Container - Hidden on mobile */}
      {!isMobile && (
        <CustomControlsContainer>
          {/* Standard Buttons */}
          <Button onClick={handleMyDeviceClick} size={ComponentSizes.MEDIUM} title="Upload from My Device">
            <Icon icon={faMobileAlt} style={{ marginRight: '5px' }} /> My Device
          </Button>
          <Button onClick={handleCameraClick} size={ComponentSizes.MEDIUM} title="Upload from Camera">
            <Icon icon={faCamera} style={{ marginRight: '5px' }} /> Camera
          </Button>
          <Button
            onClick={handleScreenCaptureToggle}
            disabled={isStartingCapture}
            size={ComponentSizes.MEDIUM}
            title={screenCaptureStream ? 'Stop Screen Capture' : 'Start Screen Capture'}
            style={{ backgroundColor: screenCaptureStream ? '#dc3545' : undefined }}
          >
            <Icon icon={faDesktop} style={{ marginRight: '5px' }} />
            {isStartingCapture ? 'Starting...' : screenCaptureStream ? 'Stop Capture' : 'Screen Capture'}
          </Button>
          <Button
            onClick={onGenerateQrCode}
            disabled={isGeneratingQrCode || filesToUpload.length > 0 || !!screenCaptureStream}
            title="Upload from Mobile via QR Code"
            size={ComponentSizes.MEDIUM}
          >
            <Icon icon={faQrcode} style={{ marginRight: '5px' }} /> Mobile QR Code
          </Button>
        </CustomControlsContainer>
      )}

      {/* Screen Capture Preview Section - Renders if screenCaptureStream is active */}
      {screenCaptureStream && (
        <CapturePreviewContainer>
          {/* <AnnotationToolbar currentTool={currentTool} setCurrentTool={setCurrentTool} currentColor={currentColor} setCurrentColor={setCurrentColor} /> */}
          <CaptureVideo ref={videoRef} autoPlay muted />
          <DrawingCanvas
            ref={drawingCanvasRef}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={() => {
              if (isDrawing) handleCanvasMouseUp({} as React.MouseEvent<HTMLCanvasElement>);
            }}
          />
          <CaptureControls>
            <Button onClick={handleCaptureAreaClick} disabled={!selectionRect || isRecording} size={ComponentSizes.MEDIUM}>
              Capture Area
            </Button>
            <Button
              onClick={isRecording ? handleStopRecording : handleStartRecording}
              disabled={isDrawing}
              size={ComponentSizes.MEDIUM}
              style={{ backgroundColor: isRecording ? '#dc3545' : undefined }}
            >
              {isRecording ? 'Stop Recording' : 'Record Video'}
            </Button>
            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
              <strong>Guide:</strong> Blue dashed line shows video content area. Red box is your selection.
            </div>
          </CaptureControls>
          {/* ++ Session Thumbnail Strip ++ */}
          {sessionCapturedFiles.length > 0 && (
            <SessionThumbnailStrip>
              {sessionCapturedFiles.map((item) => (
                <SessionThumbnailItem key={`session-${item.id}`}>
                  {item.previewUrl && item.fileType?.startsWith('image/') ? (
                    <SessionThumbnailPreview src={item.previewUrl} alt="Captured preview" />
                  ) : item.previewUrl && item.fileType === 'application/pdf' ? (
                    <iframe
                      src={item.previewUrl}
                      title={`session-pdf-${item.id}`}
                      width="80px"
                      height="80px"
                      style={{ border: 'none', backgroundColor: '#ccc' }} // Added bg for placeholder feel
                    />
                  ) : item.fileType?.startsWith('video/') ? (
                    <SessionThumbnailPlaceholder>
                      <Icon icon={faFileAlt} style={{ fontSize: '2em', color: '#888' }} />
                    </SessionThumbnailPlaceholder>
                  ) : (
                    <SessionThumbnailPlaceholder>
                      <Icon icon={faFileAlt} style={{ fontSize: '2em', color: '#888' }} />
                    </SessionThumbnailPlaceholder>
                  )}
                  <SessionThumbnailFilename title={item.customFilename || item.file.name}>{item.customFilename || item.file.name}</SessionThumbnailFilename>
                </SessionThumbnailItem>
              ))}
            </SessionThumbnailStrip>
          )}
          {/* ++ End Session Thumbnail Strip ++ */}
        </CapturePreviewContainer>
      )}

      {/* Conditional Area: Drop Zone OR File List. */}
      {/* This entire block is hidden if screen capture is active and items have been captured in the session */}
      {/* OR if screen capture is active but no items captured yet (allowing capture controls to be primary) */}
      {/* Show main list if screen capture is NOT active OR if it IS active but NO session files yet (to show prior uploads) */}
      {!screenCaptureStream || (screenCaptureStream && sessionCapturedFiles.length === 0) ? (
        <>
          {filesToUpload.length === 0 &&
            !screenCaptureStream && ( // Only show drop zone if no files AND not screen capturing
              <InitialUploadArea
                onClick={handleMyDeviceClick} // Still allow click to open file dialog
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                isDragging={isDraggingOver} // Pass dragging state for styling
                style={{ display: 'flex' }} // Always display if conditions are met
              >
                <InitialUploadIcon icon={faPlusCircle} />
                <InitialUploadText>{isMobile ? 'Tap to select files' : 'Drag & drop files here, or click to select'}</InitialUploadText>
              </InitialUploadArea>
            )}

          {/* File List & Upload Button Section */}
          {/* Show if files exist AND ( (not screen capturing) OR (screen capturing AND no session files yet) ) */}
          {/* This ensures that if you start screen capture, but haven't captured anything yet, you still see prior uploads */}
          {filesToUpload.length > 0 && !editingFile && (
            <>
              <FileListContainer>
                {filesToUpload.map((item) => (
                  <FileListItem key={item.id}>
                    {/* FileInfo with Preview/Icon */}
                    <FileInfo>
                      {item.fileType === 'application/pdf' && item.previewUrl ? (
                        <iframe src={item.previewUrl} title={item.customFilename || item.file.name} width="200px" height="200px" style={{ border: 'none' }} />
                      ) : item.previewUrl ? (
                        <FilePreview src={item.previewUrl} alt="Preview" />
                      ) : (
                        <Icon icon={faFileAlt} style={{ width: '200px', height: '200px', color: '#aaa' }} />
                      )}
                    </FileInfo>
                    {/* Metadata Inputs */}
                    <FileMetadataContainer>
                      <Input
                        label="Filename"
                        description="Edit the filename that will be saved."
                        value={item.customFilename ?? item.file.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileMetadataChange(item.id, 'customFilename', e.target.value)}
                        size={ComponentSizes.SMALL}
                      />

                      {showDescriptionField && (
                        <div>
                          <label
                            htmlFor={`description-${item.id}`}
                            style={{
                              display: 'block',
                              marginBottom: '4px',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              color: 'var(--kendo-color-on-surface, #333333)',
                            }}
                          >
                            Description
                          </label>
                          <textarea
                            id={`description-${item.id}`}
                            value={item.description ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFileMetadataChange(item.id, 'description', e.target.value)}
                            placeholder="Add a description for this file."
                            rows={3}
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              resize: 'vertical',
                              padding: '8px',
                              border: `1px solid var(--kendo-color-border, #ced4da)`,
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              fontFamily: 'inherit',
                              backgroundColor: 'var(--kendo-color-surface, #ffffff)',
                              color: 'var(--kendo-color-on-surface, #333333)',
                            }}
                          />
                        </div>
                      )}

                      <TagsField
                        label="Tags"
                        id={`tags-select-${item.id}`}
                        tags={hierarchicalTags}
                        value={item.tags.map((tagId) => tagIdMap.get(tagId)).filter((t): t is TagModel => t !== undefined)}
                        onChange={(newValue: TagModel[]) => {
                          const selectedIds = newValue.map((tag: TagModel) => tag.id);
                          handleFileMetadataChange(item.id, 'tags', selectedIds);
                        }}
                        placeholder={isLoadingTags ? 'Loading...' : 'Select tags...'}
                        // Add other necessary props for TagsField if any (e.g., initialExpandedIds, label, etc.)
                      />
                      {/* Capture Time - Added before Event Time */}
                      <DateTimeFieldsContainer>
                        <MaskedDateTimeInputField
                          label="Capture Date*"
                          description="Date of image/video capture (MM/DD/YYYY)."
                          name={`captureDate-${item.id}`}
                          mode={MASKED_DATETIME_INPUT_MODES.DATE}
                          value={item.captureDateString ?? ''}
                          onChange={(e: { value: string | null }) => handleFileMetadataChange(item.id, 'captureDateString', e.value ?? '')}
                          required
                          size={ComponentSizes.SMALL}
                        />
                        <MaskedDateTimeInputField
                          label="Capture Time*"
                          description="Time of image/video capture (hh:mm AM/PM)."
                          name={`captureTime-${item.id}`}
                          mode={MASKED_DATETIME_INPUT_MODES.TIME}
                          value={item.captureTimeString ?? ''}
                          onChange={(e: { value: string | null }) => handleFileMetadataChange(item.id, 'captureTimeString', e.value ?? '')}
                          required
                          size={ComponentSizes.SMALL}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'flex-end' }}>
                          <Label editorId={`captureTimezone-select-${item.id}`} style={{ marginBottom: '4px' }}>
                            Capture Timezone
                          </Label>
                          <select
                            id={`captureTimezone-select-${item.id}`}
                            value={item.captureTimezone || dayjs.tz.guess() || 'UTC'}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFileMetadataChange(item.id, 'captureTimezone', e.target.value)}
                            style={{
                              height: '20px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              flexGrow: 1,
                            }}
                          >
                            {commonTimezones.map((tz) => (
                              <option key={`cap-${tz}`} value={tz}>
                                {tz.replace(/_/g, ' ')}
                              </option>
                            ))}
                          </select>
                        </div>
                      </DateTimeFieldsContainer>

                      {/* Event Time - Split into Date and Time using MaskedDateTimeInputField */}
                      <DateTimeFieldsContainer>
                        <MaskedDateTimeInputField
                          label="Event Date*"
                          description="Enter the date of the relevant clinical event (MM/DD/YYYY)."
                          name={`eventDate-${item.id}`}
                          mode={MASKED_DATETIME_INPUT_MODES.DATE}
                          value={item.eventDateString ?? ''}
                          onChange={(e: { value: string | null }) => handleFileMetadataChange(item.id, 'eventDateString', e.value ?? '')}
                          required
                          size={ComponentSizes.SMALL}
                        />
                        <MaskedDateTimeInputField
                          label="Event Time*"
                          description="Enter the time of the relevant clinical event (hh:mm AM/PM)."
                          name={`eventTime-${item.id}`}
                          mode={MASKED_DATETIME_INPUT_MODES.TIME}
                          value={item.eventTimeString ?? ''}
                          onChange={(e: { value: string | null }) => handleFileMetadataChange(item.id, 'eventTimeString', e.value ?? '')}
                          required
                          size={ComponentSizes.SMALL}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'flex-end' }}>
                          <Label editorId={`eventTimezone-select-${item.id}`} style={{ marginBottom: '4px' }}>
                            Event Timezone
                          </Label>
                          <select
                            id={`eventTimezone-select-${item.id}`}
                            value={item.eventTimezone || dayjs.tz.guess() || 'UTC'}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFileMetadataChange(item.id, 'eventTimezone', e.target.value)}
                            style={{
                              height: '20px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              flexGrow: 1,
                            }}
                          >
                            {commonTimezones.map((tz) => (
                              <option key={`evt-${tz}`} value={tz}>
                                {tz.replace(/_/g, ' ')}
                              </option>
                            ))}
                          </select>
                        </div>
                      </DateTimeFieldsContainer>
                    </FileMetadataContainer>
                    {/* Action Buttons */}
                    <FileActions>
                      {item.previewUrl && (
                        <Button onClick={() => handleEditFile(item.id)} size={ComponentSizes.SMALL} title="Edit Image">
                          Edit
                        </Button>
                      )}
                      <Button onClick={() => handleRemoveFile(item.id)} size={ComponentSizes.SMALL} title="Remove File" style={{ backgroundColor: '#dc3545' }}>
                        Remove <Icon icon={faTimes} />
                      </Button>
                    </FileActions>
                  </FileListItem>
                ))}
              </FileListContainer>
              <UploadAllButtonContainer>
                <Button onClick={handleUploadAllClick} size={ComponentSizes.LARGE} title="Upload All Selected Files">
                  Upload All ({filesToUpload.length})
                </Button>
              </UploadAllButtonContainer>
            </>
          )}
        </>
      ) : null}

      {/* Enhanced Image Editor Section - Show if actively editing a file */}
      {imageSrc && editingFile && (
        <EditorContainer>
          <EditorHeader>
            <EditorTitle>Photo Editor</EditorTitle>
          </EditorHeader>

          {/* Image Filter Controls */}
          <FilterControlsWrapper>
            <FilterControlsContainer style={{ margin: '0' }}>
              {/* Rotation Controls */}
              <FilterControlGroup>
                <FilterLabel>Rotation</FilterLabel>
                <FilterButtonGroup>
                  <FilterButton onClick={() => rotateEditingImage(-90)} size={ComponentSizes.SMALL}>
                    ↺ 90°
                  </FilterButton>
                  <FilterButton onClick={() => rotateEditingImage(90)} size={ComponentSizes.SMALL}>
                    ↻ 90°
                  </FilterButton>
                </FilterButtonGroup>
              </FilterControlGroup>

              {/* Flip Controls */}
              <FilterControlGroup>
                <FilterLabel>Flip</FilterLabel>
                <FilterButtonGroup>
                  <FilterButton
                    onClick={() => updateEditingFilter('flipHorizontal', !editingFilters.flipHorizontal)}
                    $active={editingFilters.flipHorizontal}
                    size={ComponentSizes.SMALL}
                  >
                    ↔ H
                  </FilterButton>
                  <FilterButton
                    onClick={() => updateEditingFilter('flipVertical', !editingFilters.flipVertical)}
                    $active={editingFilters.flipVertical}
                    size={ComponentSizes.SMALL}
                  >
                    ↕ V
                  </FilterButton>
                </FilterButtonGroup>
              </FilterControlGroup>

              {/* Brightness Control */}
              <FilterControlGroup>
                <FilterLabel>Brightness: {editingFilters.brightness}%</FilterLabel>
                <FilterSlider
                  type="range"
                  min="0"
                  max="200"
                  value={editingFilters.brightness}
                  onChange={(e) => updateEditingFilter('brightness', parseInt(e.target.value))}
                />
              </FilterControlGroup>

              {/* Contrast Control */}
              <FilterControlGroup>
                <FilterLabel>Contrast: {editingFilters.contrast}%</FilterLabel>
                <FilterSlider
                  type="range"
                  min="0"
                  max="200"
                  value={editingFilters.contrast}
                  onChange={(e) => updateEditingFilter('contrast', parseInt(e.target.value))}
                />
              </FilterControlGroup>

              {/* Saturation Control */}
              <FilterControlGroup>
                <FilterLabel>Saturation: {editingFilters.saturation}%</FilterLabel>
                <FilterSlider
                  type="range"
                  min="0"
                  max="200"
                  value={editingFilters.saturation}
                  onChange={(e) => updateEditingFilter('saturation', parseInt(e.target.value))}
                />
              </FilterControlGroup>

              {/* Reset Button */}
              <FilterControlGroup style={{ justifyContent: 'flex-end' }}>
                <ResetButton onClick={resetEditingFilters} size={ComponentSizes.SMALL}>
                  Reset All
                </ResetButton>
              </FilterControlGroup>
            </FilterControlsContainer>
          </FilterControlsWrapper>

          {/* Image with Crop Tool and Filters Applied */}
          <CropPreview>
            <Cropper ref={cropperRef} src={transformedImageSrc || imageSrc} className={'cropper'} />
            <AnnotationCanvas
              ref={annotationCanvasRef}
              width={0}
              height={0}
              className={isAnnotating ? 'active' : ''}
              // TODO: Add annotation mouse handlers
            />
          </CropPreview>

          {/* Real-time Filter Preview */}

          <EditorFooter>
            <Button
              onClick={() => {
                setEditingFile(null);
                setImageSrc(null);
                setAnnotations([]);
                resetEditingFilters();
              }}
              size={ComponentSizes.MEDIUM}
              style={{ backgroundColor: '#6c757d' }}
            >
              Cancel Edit
            </Button>
            <Button
              onClick={() => {
                // Download the current cropped image with filters applied
                const cropCanvas = cropperRef.current?.getCanvas();
                if (cropCanvas && editingFile) {
                  cropCanvas.toBlob((blob) => {
                    if (blob) {
                      const link = document.createElement('a');
                      link.download = `edited-${editingFile.file.name}`;
                      link.href = URL.createObjectURL(blob);
                      link.click();
                      URL.revokeObjectURL(link.href);
                    }
                  }, 'image/png');
                }
              }}
              size={ComponentSizes.MEDIUM}
            >
              Download Preview
            </Button>
            <Button onClick={handleConfirmCrop} size={ComponentSizes.MEDIUM}>
              Apply Changes
            </Button>
          </EditorFooter>
        </EditorContainer>
      )}

      {/* Webcam Modal */}
      {showWebcamModal && (
        <WebcamModalOverlay onClick={handleWebcamClose}>
          <WebcamModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width={640}
              videoConstraints={{ width: 1280, height: 720, facingMode: 'user' }}
            />
            <WebcamControls>
              <Button onClick={handleWebcamCapture} size={ComponentSizes.SMALL}>
                Capture Photo
              </Button>
              <Button onClick={handleWebcamClose} style={{ backgroundColor: '#6c757d' }} size={ComponentSizes.SMALL}>
                Cancel
              </Button>
            </WebcamControls>
          </WebcamModalContent>
        </WebcamModalOverlay>
      )}
    </UploadContainer>
  );
};
